import { useRouter } from '@tanstack/react-router';
import { ConnectorRouteView, JourneysConnectorHandoff, useConnectorHandoffHrefs } from '@/features/integrations';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { isNonCatalogIntegrationSegment } from '@/services/router/integration-segments';
import ExternalIntegrationPage from './index.page';

type ExternalIntegrationRouteProps = {
  /** The catalog id from the URL. */
  type: string;
};

/** The two spellings of this page's URL, for the catalog id in hand. */
function useHandoffHrefs(type: string) {
  const router = useRouter();

  return useConnectorHandoffHrefs({
    buildMigrated: (workspaceId) =>
      router.buildLocation({ to: '/workspaces/$workspaceId/integrations/$type', params: { workspaceId, type } }).href,
    buildLegacy: (workspaceId) =>
      router.buildLocation({
        to: '/workspaces/$workspaceId/journeys/integrations/$type',
        params: { workspaceId, type },
      }).href,
  });
}

/**
 * Whether this page serves the segment in hand.
 *
 * The route matches any single segment, so a sibling connector's URL lands here
 * too — `/integrations/segment_in` and its `journeys` twin among them. Those
 * belong to whoever owns them, so the page reports itself unmigrated for them and
 * both directions hand the URL back to the embedded app.
 */
function useServesSegment(type: string): boolean {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.IntegrationConnectorsMigration);

  return isMigrated && !isNonCatalogIntegrationSegment(type);
}

/** The old `/journeys/integrations/:type` URL. */
export function JourneysExternalIntegrationHandoff({ type }: ExternalIntegrationRouteProps) {
  const isMigrated = useServesSegment(type);
  const { migrated } = useHandoffHrefs(type);

  return <JourneysConnectorHandoff isMigrated={isMigrated} migratedHref={migrated} />;
}

/**
 * The migrated `/integrations/:type` URL.
 *
 * `ConnectorRouteView` applies the gates Ember's `integrations` root route
 * applied to everything nested under it. This page's own route added no further
 * gate, so nothing nests inside it here.
 */
export function ExternalIntegrationRouteView({ type }: ExternalIntegrationRouteProps) {
  const isMigrated = useServesSegment(type);
  const { legacy } = useHandoffHrefs(type);

  return (
    <ConnectorRouteView isMigrated={isMigrated} legacyHref={legacy}>
      {(workspaceId) => <ExternalIntegrationPage type={type} workspaceId={workspaceId} />}
    </ConnectorRouteView>
  );
}
